import { createFetchApi, FetchMiddlewares } from '@backstage/core-app-api';
import {
  isOidcSessionLogoutSignal,
  OIDC_SESSION_KEY_MISSING,
  OIDC_SESSION_REVOKED,
  OIDC_SESSION_SIGNAL_CHANNEL,
} from 'adp-oidc-common';
import type {
  ConfigApi,
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import type {
  SignalApi,
  SignalSubscriber,
} from '@backstage/plugin-signals-react';

type LogoutSource = 'response' | 'signal';

export type OidcSessionLogoutCoordinator = {
  request(options: { source: LogoutSource }): Promise<void>;
};

export type OidcSessionLogoutSignalSubscriptionOptions = {
  signals: SignalApi;
  coordinator: OidcSessionLogoutCoordinator;
  getCurrentSessionKey: () => Promise<string | undefined>;
};

export function createOidcSessionLogoutCoordinator(options: {
  signOut: () => Promise<void>;
  navigate: () => void;
}): OidcSessionLogoutCoordinator {
  let signOutPromise: Promise<void> | undefined;
  let hasNavigated = false;

  const navigateOnce = () => {
    if (!hasNavigated) {
      hasNavigated = true;
      options.navigate();
    }
  };

  const startSignOut = () => {
    if (!signOutPromise) {
      try {
        signOutPromise = Promise.resolve(options.signOut()).catch(
          () => undefined,
        );
      } catch {
        signOutPromise = Promise.resolve();
      }
    }

    return signOutPromise;
  };

  return {
    request({ source }) {
      const currentSignOut = startSignOut();

      if (source === 'signal') {
        navigateOnce();
      } else {
        void currentSignOut.then(navigateOnce);
      }

      return currentSignOut.then(() => undefined);
    },
  };
}

export function createRevocationAwareFetchApi(options: {
  fetchApi: FetchApi;
  coordinator: OidcSessionLogoutCoordinator;
}): FetchApi {
  return {
    async fetch(input, init) {
      const response = await options.fetchApi.fetch(input, init);

      if (response.status === 401) {
        await handleRevocationResponse(response, options.coordinator);
      }

      return response;
    },
  };
}

export function createOidcSessionFetchApi(options: {
  configApi: ConfigApi;
  identityApi: IdentityApi;
  discoveryApi: DiscoveryApi;
  oidcAuthApi: { signOut: () => Promise<void> };
  coordinator?: OidcSessionLogoutCoordinator;
  baseImplementation?: typeof fetch;
}): FetchApi {
  const coordinator =
    options.coordinator ??
    getAppOidcSessionLogoutCoordinatorFromApis({
      oidcAuthApi: options.oidcAuthApi,
      configApi: options.configApi,
    });

  return createRevocationAwareFetchApi({
    // This is an app-level copy of the default Backstage fetch factory because
    // the revocation decorator must wrap the resolved FetchApi. Keep the
    // middleware list and its order synchronized with
    // `plugins/app/src/defaultApis.ts` after every Backstage upgrade.
    fetchApi: createFetchApi({
      baseImplementation: options.baseImplementation,
      middleware: [
        FetchMiddlewares.resolvePluginProtocol({
          discoveryApi: options.discoveryApi,
        }),
        FetchMiddlewares.injectIdentityAuth({
          identityApi: options.identityApi,
          config: options.configApi,
        }),
      ],
    }),
    coordinator,
  });
}

async function handleRevocationResponse(
  response: Response,
  coordinator: OidcSessionLogoutCoordinator,
): Promise<void> {
  let body: unknown;

  try {
    body = await response.clone().json();
  } catch {
    return;
  }

  const code = getErrorCode(body);
  if (code !== OIDC_SESSION_REVOKED && code !== OIDC_SESSION_KEY_MISSING) {
    return;
  }

  await coordinator.request({ source: 'response' });
}

function getErrorCode(body: unknown): string | undefined {
  if (!body || typeof body !== 'object' || !('error' in body)) {
    return undefined;
  }

  const error = body.error;
  if (!error || typeof error !== 'object' || !('code' in error)) {
    return undefined;
  }

  return typeof error.code === 'string' ? error.code : undefined;
}

export function createOidcSessionLogoutSignalSubscription(
  options: OidcSessionLogoutSignalSubscriptionOptions,
): SignalSubscriber {
  let isActive = true;
  const subscription = options.signals.subscribe(
    OIDC_SESSION_SIGNAL_CHANNEL,
    message => {
      if (!isActive) {
        return;
      }

      if (isOidcSessionLogoutSignal(message)) {
        void options
          .getCurrentSessionKey()
          .then(currentSessionKey => {
            if (!isActive || currentSessionKey !== message.sessionKey) {
              return undefined;
            }

            return options.coordinator.request({ source: 'signal' });
          })
          .catch(() => undefined);
      }
    },
  );

  return {
    unsubscribe() {
      isActive = false;
      subscription.unsubscribe();
    },
  };
}

export function getOidcSessionKeyFromToken(
  token: string | undefined,
): string | undefined {
  if (!token) {
    return undefined;
  }

  const payload = token.split('.')[1];
  if (!payload) {
    return undefined;
  }

  try {
    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (normalizedPayload.length % 4)) % 4);
    const claims: unknown = JSON.parse(atob(`${normalizedPayload}${padding}`));

    if (
      claims &&
      typeof claims === 'object' &&
      'oidc_session_key' in claims &&
      typeof claims.oidc_session_key === 'string'
    ) {
      return claims.oidc_session_key;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

const appCoordinatorsByAuthApi = new WeakMap<
  object,
  OidcSessionLogoutCoordinator
>();

export function getAppOidcSessionLogoutCoordinatorFromApis(options: {
  oidcAuthApi: { signOut: () => Promise<void> };
  configApi: { getOptionalString: (key: string) => string | undefined };
}): OidcSessionLogoutCoordinator {
  const existingCoordinator = appCoordinatorsByAuthApi.get(options.oidcAuthApi);
  if (existingCoordinator) {
    return existingCoordinator;
  }

  const coordinator = createOidcSessionLogoutCoordinator({
    signOut: () => options.oidcAuthApi.signOut(),
    navigate: createOidcSessionNavigation(
      options.configApi.getOptionalString('app.baseUrl') ??
        window.location.origin,
    ),
  });

  appCoordinatorsByAuthApi.set(options.oidcAuthApi, coordinator);
  return coordinator;
}

export function createOidcSessionNavigation(baseUrl: string): () => void {
  return () => window.location.assign(baseUrl);
}
